import { Campaign, GetCampaignParams } from '@features/campaigns';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@features/users';
import { buildPresentation } from '@features/lci-report/subdomains/cpg/utils/build-presentation';
import { buildPresentationV2 } from '@features/lci-report/subdomains/cpg/utils/v2/build-presentation';
import { useCpgLciReportFields } from '@features/lci-report/subdomains/cpg/hooks/useCpgLciReportFields';
import { CpgReportsParams } from '@features/campaigns/subdomains/cpg/api/api';
import {
  clientDefaultValues,
  clientDefaultValuesV2,
} from '@features/lci-report/subdomains/cpg/utils/client-default-values';
import { presentationToFile } from '@features/powerpoint';
import { stringify } from 'qs';
import { LciReportDownloadButton } from './LciReportDownloadButton';
import { useFeatures } from '@utils/hooks';

type CpgLciReportDownloadButtonProps = {
  campaignId: Campaign['id'];
  reportParams: Omit<CpgReportsParams, 'campaignId'>;
  campaignParams: GetCampaignParams;
};

export const CpgLciReportDownloadContainer = (
  props: CpgLciReportDownloadButtonProps
) => {
  const navigate = useNavigate();
  const { role: userRole } = useCurrentUser();
  const features = useFeatures();
  const { pptRedesign } = features;

  const { data, loading } = useCpgLciReportFields({
    campaignParams: {
      ...props.campaignParams,
      id: props.campaignId,
    },
    reportParams: props.reportParams,
  });

  const handleDownload = () => {
    if (!data) throw new Error('Missing data for presentation');

    const presentation = pptRedesign
      ? buildPresentationV2({
          data: { ...data, brandLogo: null },
          customFields: clientDefaultValuesV2(data.report, data.campaign.name),
          features,
        })
      : buildPresentation({
          data: { ...data, brandLogo: null },
          customFields: clientDefaultValues(data.report, data.campaign.name),
          features,
        });

    if (userRole === 'user') {
      void presentationToFile(presentation);
    } else {
      void navigate(
        `/campaigns/cpg/${props.campaignId}/lci-report?${stringify({ ...props.campaignParams, ...props.reportParams })}`
      );
    }
  };

  return (
    <LciReportDownloadButton
      loading={loading}
      userRole={userRole}
      onDownload={handleDownload}
      disabled={loading}
    />
  );
};
